// SaudiMarketInsights.jsx — v200.30 · شاشة صحة قاعدة العملاء (2026-02-09)
//
// v200.30 owner ruling (2026-02-09): كل حساب في هذه الشاشة يُنفَّذ على
// الخادم من `pos_sales` عبر `pos_aggregate`، بالتجميع على
// `customer_phone_key`. لا حساب مالي ولا عددي داخل هذا المكوّن.
// مصدر الأرقام: GET /api/customers/health-snapshot
//
// كل رقم يُعلن: نافذته · وضعه · تغطيته — كما اشترط المالك (البند ٣).
// اللوحة السبع المعتمدة فقط:
//   Gold #AA9566 · Deep #3D2A1C · Cream #F7F2EC · White #FFFFFF ·
//   Data-Green #0F766E · Data-Red #B91C1C · Data-Amber #B45309.

import React, { useEffect, useState } from "react";
import { api } from "./lib/api";
import { InfoTip } from "./components/InfoTip";

// ── اللوحة السبع الرسمية ──
const GOLD  = "#AA9566";
const DEEP  = "#3D2A1C";
const CREAM = "#F7F2EC";
const WHITE = "#FFFFFF";
const OK    = "#0F766E";
const DOWN  = "#B91C1C";
const WARN  = "#B45309";

const fm = (n, d = 0) =>
  (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: d });
const fmPct = (v, d = 1) => `${fm((v || 0) * 100, d)}%`;

function Stat({ label, value, window: win, mode, coverage, tone, testid }) {
  const color = tone === "danger" ? "#F5A9A9"
              : tone === "warn"   ? "#F0C87A"
              : tone === "ok"     ? "#9EE0C9"
              : WHITE;
  return (
    <div
      data-testid={testid}
      style={{
        background: DEEP,
        border: `1px solid ${GOLD}`,
        borderRadius: 12,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minWidth: 190,
      }}
    >
      <div style={{ color: CREAM, fontSize: 12, fontWeight: 700, opacity: 0.95 }}>{label}</div>
      <div style={{ color, fontSize: 26, fontWeight: 900, lineHeight: 1.1 }}>{value}</div>
      <div style={{ color: CREAM, fontSize: 11, lineHeight: 1.8, opacity: 0.9 }}>
        {win && <div data-testid={`${testid}-window`}>🕒 <b>النافذة:</b> {win}</div>}
        {mode && <div data-testid={`${testid}-mode`}>⚙ <b>الوضع:</b> {mode}</div>}
        {coverage && <div data-testid={`${testid}-coverage`}>📊 <b>التغطية:</b> {coverage}</div>}
      </div>
    </div>
  );
}

export default function SaudiMarketInsights() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // جولة تصحيح 2026-10-14 (المرحلة 2، البند 6) — بلا 14 ثابتة هنا؛
        // الخادم يقرأ customer_hub_active_window_days من tenant_settings.
        const r = await api.get("/customers/health-snapshot");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(String(e?.response?.data?.detail || e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="card" data-testid="saudi-market-insights" style={{ padding: 16, background: WHITE, border: `1px solid ${GOLD}`, borderRadius: 12 }}>
        <div style={{ color: DEEP, fontSize: 13 }}>⏳ جارٍ حساب الأرقام من pos_sales...</div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="card" data-testid="saudi-market-insights" style={{ padding: 16, background: WHITE, border: `1px solid ${DOWN}`, borderRadius: 12 }}>
        <div style={{ color: DOWN, fontSize: 13, fontWeight: 800 }} data-testid="smi-error">خطأ: {err}</div>
      </div>
    );
  }
  if (!data) return null;

  const covPctText = `${fmPct(data.coverage?.ratio, 1)} — ${fm(data.coverage?.docs_with_identity)} من ${fm(data.coverage?.docs_total)} فاتورة`;

  // تشخيص المستشار — يقرأ الأرقام كما هي من الخادم، لا يُعيد حسابها.
  const s = data.segments || {};
  const healthy_pct = (s.healthy_pct || 0) * 100;
  const risky_pct = (s.risky_pct || 0) * 100;
  const drifter_count = s.counts?.drifter || 0;
  const drifter_pct = (data.totals?.customers || 0) > 0
    ? (drifter_count / data.totals.customers) * 100
    : 0;
  const single_pct = (data.single_visit?.share || 0) * 100;
  const avg_visits = data.averages?.visits_per_customer || 0;
  const gold = s.counts?.gold_vip || 0;
  const at_risk = s.counts?.at_risk || 0;
  const rising_plus_new = (s.counts?.rising_star || 0) + (s.counts?.new || 0);
  const premium_share = (data.aov_distribution?.premium?.share || 0) * 100;
  // v200.39 · لا لفظ كسر محفور — الأرقام تُعرَض بنسبتها المئوية
  // مباشرةً («41.1٪ من القاعدة») بدل «ثلث/ربع/نصف». الحارس يمسك
  // الإدخال المستقبلي لأي كلمة كسر عربية في هذا الملف بالكامل.
  return (
    <div className="card" data-testid="saudi-market-insights" style={{ padding: 0, overflow: "hidden", marginBottom: 14, background: WHITE, border: `1px solid ${GOLD}`, borderRadius: 12 }}>
      {/* Header */}
      <div style={{ padding: "14px 18px", background: DEEP, color: WHITE, borderBottom: `2px solid ${GOLD}` }}>
        <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 700 }}>🇸🇦 رؤية تحليلية احترافية — السوق السعودي</div>
        <h3 style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 800, color: WHITE }}>صحة قاعدة العملاء</h3>
        <div data-testid="smi-source" style={{ marginTop: 6, fontSize: 12, color: CREAM }}>
          🔗 <b>المصدر:</b> {data.source?.label_ar}
        </div>
      </div>

      <div style={{ padding: 16, display: "grid", gap: 14 }}>
        {/* Stats grid — كل رقم بنافذته ووضعه وتغطيته */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
          <Stat
            testid="smi-total"
            label="👥 إجمالي العملاء"
            value={fm(data.totals?.customers)}
            window={data.totals?.window_ar}
            mode={data.totals?.mode_ar}
            coverage={covPctText}
          />
          <Stat
            testid="smi-healthy"
            label="🟢 شريحة صحية"
            value={fmPct(s.healthy_pct)}
            tone="ok"
            window={s.window_ar}
            mode={s.mode_ar}
            coverage={`${fm(s.healthy)} عميل صحّي`}
          />
          <Stat
            testid="smi-risky"
            label="🔴 شريحة معرّضة"
            value={fmPct(s.risky_pct)}
            tone="danger"
            window={s.window_ar}
            mode={s.mode_ar}
            coverage={`${fm(s.risky)} عميل معرّض`}
          />
          <Stat
            testid="smi-drifter"
            label="🍃 عابر"
            value={fm(drifter_count)}
            tone="warn"
            window="46-90 يوماً"
            mode="زيارة واحدة في تلك النافذة — بين النموّ والخطر"
            coverage={`${fm(drifter_pct, 1)}% من العملاء ذوي الهوية · الحاصل الحسابي: صحية + معرّضة + عابر = ${fm((s.healthy || 0) + (s.risky || 0) + drifter_count)}`}
          />
          <Stat
            testid="smi-recent14"
            label={`📅 نشطون آخر ${data.active_in_window?.window_days} يوم`}
            value={fm(data.active_in_window?.count)}
            window={data.active_in_window?.window_ar}
            mode={`${data.active_in_window?.mode_ar} · ${data.active_in_window?.bound_ar || ""}`}
            coverage={`${fmPct(data.active_in_window?.share, 1)} من العملاء ذوي الهوية`}
          />
          <Stat
            testid="smi-avg-visits"
            label="📈 متوسط زيارات العميل"
            value={fm(data.averages?.visits_per_customer, 2)}
            window={data.averages?.window_ar}
            mode={data.averages?.mode_ar}
            coverage={`متوسط الإنفاق: ${fm(data.averages?.spend_per_customer, 2)} ر.س`}
          />
          <Stat
            testid="smi-single-visit"
            label="🚪 ضيف لمرة واحدة"
            value={fmPct(data.single_visit?.share)}
            tone={single_pct > 40 ? "danger" : "ok"}
            window={data.single_visit?.window_ar}
            mode={data.single_visit?.mode_ar}
            coverage={`${fm(data.single_visit?.count)} عميل`}
          />
        </div>

        {/* AOV distribution — بدون حساب داخل المكوّن */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: DEEP, marginBottom: 8 }}>
            💎 توزيع متوسط فاتورة العميل (AOV) — {data.aov_distribution?.window_ar} · {data.aov_distribution?.mode_ar}
          </div>
          <div style={{ display: "flex", gap: 4, height: 28, borderRadius: 999, overflow: "hidden", border: `1px solid ${GOLD}` }}>
            {["entry", "mid", "high", "premium"].map((k) => {
              const share = data.aov_distribution?.[k]?.share || 0;
              const pct = share * 100;
              if (pct < 0.5) return null;
              const color = k === "entry" ? WARN : k === "mid" ? OK : k === "high" ? DEEP : GOLD;
              return (
                <div key={k}
                  data-testid={`smi-aov-${k}`}
 
                  style={{
                    width: `${pct}%`, background: color, color: WHITE,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 800,
                  }}><InfoTip text={`${data.aov_distribution?.tiers_ar?.[k]}: ${fm(data.aov_distribution?.[k]?.count)} (${fm(pct, 1)}%)`} />
                  {pct >= 8 ? `${fm(pct, 0)}%` : ""}
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8, fontSize: 11, color: DEEP }}>
            {["entry", "mid", "high", "premium"].map((k) => (
              <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 10, height: 10, background: k === "entry" ? WARN : k === "mid" ? OK : k === "high" ? DEEP : GOLD, borderRadius: 2 }} />
                {data.aov_distribution?.tiers_ar?.[k]} ({fm(data.aov_distribution?.[k]?.count)})
              </span>
            ))}
          </div>
        </div>

        {/* Lost-scope disclosure */}
        <div
          data-testid="smi-lost-scope"
          style={{
            padding: "8px 12px", background: CREAM, border: `1px dashed ${GOLD}`,
            borderRadius: 8, color: DEEP, fontSize: 12, lineHeight: 1.8,
          }}
        >
          📌 <b>نطاق «الفاقد»:</b> {s.lost_scope_ar}
        </div>

        {/* Consultant diagnosis — تُقرأ من أرقام الخادم فقط */}
        <div style={{
          padding: 12, borderRadius: 10, background: DEEP, color: WHITE,
          border: `1px solid ${GOLD}`,
        }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: CREAM, marginBottom: 6 }}>
            🧑‍💼 تشخيص المستشار:
          </div>
          <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 12.5, lineHeight: 1.9, color: WHITE }}>
            {healthy_pct >= 60 && (
              <li>✅ قاعدة عملاء قوية ({fm(healthy_pct, 1)}% صحية) — ركّز على رفع AOV.</li>
            )}
            {risky_pct >= 30 && (
              <li data-testid="smi-diag-risky">
                ⚠ {fm(risky_pct, 1)}٪ من القاعدة في خطر أو مفقود
                {" "}({fm(s.risky)} عميل) — يجب تخصيص ميزانية شهرية
                لاسترجاع «في خطر».
              </li>
            )}
            {drifter_count > 0 && (
              <li data-testid="smi-diag-drifter">
                🍃 {fm(drifter_count)} عميل «عابر» ({fm(drifter_pct, 1)}%)
                — زاروك مرّة واختفوا 46-90 يوماً؛ ليسوا في خطر بعد ولا
                معافَون. جرِّب رسالة تعارف قبل أن يتحولوا لمفقود.
              </li>
            )}
            {single_pct > 40 && (
              <li>🚨 {fm(single_pct, 1)}% زاروا مرة واحدة — التجربة الأولى ضعيفة.</li>
            )}
            {gold > 0 && (
              <li>👑 {fm(gold)} عميل ذهبي — خصّص لهم برنامج VIP.</li>
            )}
            {premium_share >= 10 && (
              <li>💎 {fm(premium_share, 1)}% AOV مميّز — احتمالية طلبات جماعية عالية.</li>
            )}
            {at_risk > 0 && (
              <li>🚨 {fm(at_risk)} عميل VIP في خطر — اتصل بهم شخصياً اليوم.</li>
            )}
            {rising_plus_new > 0 && (
              <li>🌱 {fm(rising_plus_new)} عميل في مرحلة النمو — تواصل خلال 7 أيام.</li>
            )}
            {avg_visits < 2 && (data.totals?.customers || 0) >= 50 && (
              <li>📉 متوسط زيارات العميل أقل من 2 — قاعدة العملاء غير ولائية بعد.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
